import DealsApi from "@apis/deals";
import { deletesvg, edit, switchOff, switchOn } from "@assets/icons";
import Header from "@commons/header";
import Svg from "@components/svg";
import Table from "@components/table";
import Wrapper from "@components/wrapper";
import Button from "@components/button";
import Row from "@components/row";
import { useModal } from "@context/ModalContext";
import useConfig from "@hooks/useConfig";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteModal from "@components/delete-modal";
import Helper from "@utils/helper";
import { Plus } from "lucide-react";
import usePermission from "@hooks/usePermission";
import { toast } from "react-toastify";

const DealsList = () => {
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const { showModal } = useModal();
  const { hasPermission } = usePermission();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const res: any = await DealsApi.list({
        params: { search, page, limit: 30 },
      });
      if (res) {
        setData(res.items || res || []);
        setTotalItems(res.totalItems || 0);
        setTotalPages(res.totalPages || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, [page, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const columns = [
    {
      title: "SN",
      key: "sn",
      render: (_: any, index: number) => index + 1,
    },
    {
      title: "Title",
      key: "title",
      render: (item: any) => item.title,
    },
    {
      title: "Cover Image",
      key: "coverImage",
      render: (item: any) =>
        item.coverImage ? (
          <img
            src={`${bucketUrl}/${item.coverImage}`}
            alt="deal"
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 4,
            }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      title: "Store Count",
      key: "storeCount",
      render: (item: any) => item.storeCount || 0,
    },
    {
      title: "Priority",
      key: "priority",
      render: (item: any) => item.priority || 0,
    },
    {
      title: "Validity",
      key: "validity",
      render: (item: any) => (
        <div
          style={{
            fontSize: 13,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div>
            <span style={{ fontWeight: 500 }}>Start:</span>{" "}
            {Helper.formatDate(item.startDate)} {item.startTime}
          </div>
          <div>
            <span style={{ fontWeight: 500 }}>End:</span>{" "}
            {Helper.formatDate(item.endDate)} {item.endTime}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "isActive",
      render: (item: any) => (
        <div
          onClick={async () => {
            if (!hasPermission("deals", "Edit")) {
              toast.error("You don't have permission to edit deals");
              return;
            }
            try {
              await DealsApi.update({
                id: item.id,
                data: { isActive: !item.isActive },
              });
              fetchDeals(); // Refresh
            } catch (e) {
              console.error("Failed to toggle status", e);
            }
          }}
          style={{ cursor: "pointer", display: "inline-block" }}
          title="Toggle Status"
        >
          <Svg src={item.isActive ? switchOn : switchOff} />
        </div>
      ),
    },
    {
      title: "Products",
      key: "products",
      render: (item: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {hasPermission("deals", "Manage Products") ? (
            <Button
              title="Manage"
              onClick={() => navigate(`/admin/deals/${item.id}/products`)}
              width={100}
              height={32}
              styleButton="font-size: 12px; font-weight: 500; border-radius: 4px; background-color: #fff; color: red; border: 1px solid red;"
            />
          ) : (
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              No permission
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <Wrapper>
      <Header title="Zedfo Deals" />
      <Row styleRow="justify-content: flex-end; margin-bottom: 20px;">
        {hasPermission("deals", "Add Deal") && (
          <Button
            title="Add Deal"
            onClick={() => navigate("/admin/deals/add")}
          />
        )}
      </Row>
      <Table
        loading={loading}
        columns={columns}
        data={data}
        hasSearchbar={true}
        onSearchChange={setSearch}
        totalItems={totalItems}
        totalPages={totalPages}
        itemsPerPage={30}
        pageControlled={page}
        onPageChange={setPage}
        actions={[
          ...(hasPermission("deals", "Edit")
            ? [
                {
                  key: "edit",
                  icon: edit,
                  onClick: (item: any) => navigate(`/admin/deals/${item.id}`),
                },
              ]
            : []),
          ...(hasPermission("deals", "Delete")
            ? [
                {
                  key: "delete",
                  icon: deletesvg,
                  onClick: (item: any) =>
                    showModal(
                      <DeleteModal
                        api={DealsApi}
                        id={item.id}
                        state="deals"
                        onSuccess={fetchDeals}
                      />,
                    ),
                },
              ]
            : []),
        ]}
      />
    </Wrapper>
  );
};

export default DealsList;
